import { Download, EyeVisible, Link, Replace } from '@factorialco/f0-react/icons/app'
import { QuotesTabsVisibilityQuery } from 'generated/resources/graphql'
import { compact } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import {
  quoteStatusAsVariant,
  quoteTranslateStatus,
  quoteTranslateType,
} from 'modules/finance/components/Quotes/utils'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

export const buildQuoteHeaderProps = (
  canSeeProjects: boolean,
  locale: string,
  quote: NonNullable<QuotesTabsVisibilityQuery['finance']['quote']>
): ResourceHeadersPropsWithActions => {
  const canCreateProject = quote._policies?.convertToProject?.can
  const projectAction = canCreateProject
    ? {
        label: 'Convert to project',
        icon: Replace,
        key: 'convert_to_project',
      }
    : quote.projectId && canSeeProjects
    ? {
        label: i18n.t('finance.quotes.actions.go_to_project'),
        icon: Link,
        key: 'go_to_project',
      }
    : undefined

  const invoiceAction =
    quote.status !== 'rejected' &&
    !quote.financialDocumentAssociation?.financialDocument &&
    quote._policies?.convertToInvoice?.can
      ? {
          label: i18n.t('finance.quotes.actions.convert_to_invoice'),
          icon: Replace,
          key: 'convert_to_invoice',
        }
      : quote.financialDocumentAssociation?.financialDocument
      ? {
          label: i18n.t('finance.quotes.actions.go_to_invoice'),
          icon: Link,
          key: 'go_to_invoice',
        }
      : undefined

  const primaryAction = !!projectAction
    ? projectAction
    : !quote.projectId
    ? invoiceAction
    : undefined

  const secondaryActions = compact([
    {
      label: 'Download PDF',
      icon: Download,
      key: 'download_quote',
    },
    {
      label: 'Preview',
      icon: EyeVisible,
      key: 'open_preview',
    },
    primaryAction,
  ])

  return {
    title: quote.name ?? quote.quoteNumber ?? '-',
    status: {
      label: i18n.t('finance.sale_invoices.detail.header.status'),
      text: quoteTranslateStatus(quote.status),
      variant: quoteStatusAsVariant(quote.status),
    },
    metadata: [
      {
        label: 'Client',
        value: {
          type: 'text',
          content: contactFullName(quote.client),
        },
      },
      {
        label: 'Nº',
        value: {
          type: 'text',
          content: quote.quoteNumber ?? '-',
        },
      },
      {
        label: 'Budget',
        value: {
          type: 'text',
          content: quoteTranslateType(quote.quoteType),
        },
      },
      {
        label: 'Deal value',
        value: {
          type: 'text',
          content: quote.deal
            ? formattedAmount({
                amountCents: Number(quote.deal?.estimatedAmountCents),
                currency: quote.deal?.currency ?? '',
                locale,
              })
            : '-',
        },
      },
    ],
    secondaryActions,
  }
}
